import { useState } from 'react'
import { Loader2, AlertCircle, Info } from 'lucide-react'
import { useFeatureFlagsAdmin } from '@/hooks/useFeatureFlags'
import { useTenant } from '@/providers/TenantProvider'
import { usePermission, PERMISSIONS } from '@/lib/permissions'
import { Card, CardContent } from '@/components/ui/card'
import { Switch } from '@/components/ui/switch'

/**
 * Edits the feature flags of the tenant selected in the TopBar, deliberately
 * WITHOUT a picker of its own.
 *
 * This panel used to carry a second tenant `<Select>` populated from the very
 * same `tenants` array the TopBar switcher renders. It bought no extra reach
 * (the tab is superadmin-only, and a superadmin already sees every tenant up
 * there) and put two identical controls on screen with nothing saying which one
 * the switches obey. It also read `selectedTenantId` into state once and never
 * resynced, staying correct only because `Layout` keys `<Outlet>` on the tenant
 * id and remounts this subtree — an incidental dependency that would have
 * silently started writing flags to the wrong tenant the day that remount
 * changed.
 *
 * Following the TopBar instead means the tenant you are acting as is the tenant
 * you are configuring, so a toggle's effect shows up in your own UI straight
 * away. Turning a module off does degrade your own session, which is the point:
 * it is what that tenant's users will see. `/configure` is never feature-gated
 * (see `NAV_FEATURES` and the router), so the way back is open whatever you
 * switch off.
 */
export function FeatureFlagsSettings() {
  const { can } = usePermission()
  const canManage = can(PERMISSIONS.featureFlagsManage)

  const { selectedTenantId, selectedTenant } = useTenant()
  const { list, setFlag } = useFeatureFlagsAdmin(selectedTenantId)
  const features = list.data ?? []

  const [rowError, setRowError] = useState<string | null>(null)

  if (!canManage) {
    return (
      <div className='flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800'>
        <AlertCircle className='h-4 w-4 shrink-0' />
        You do not have permission to manage feature flags.
      </div>
    )
  }

  function handleToggle(featureKey: string, enabled: boolean) {
    setRowError(null)
    setFlag.mutate(
      { featureKey, enabled },
      {
        onError: e =>
          setRowError(e instanceof Error ? e.message : 'Could not update feature flag.'),
      },
    )
  }

  return (
    <div className='space-y-4'>
      <div>
        <h2 className='text-base font-semibold text-foreground'>
          Feature flags for {selectedTenant?.name ?? 'this tenant'}
        </h2>
        <p className='text-sm text-muted-foreground'>
          Switch modules on or off for the tenant you are working in. To configure a different
          tenant, switch to it at the top of the page.
        </p>
      </div>

      <div className='flex items-center gap-2 rounded-md border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground'>
        <Info className='h-3.5 w-3.5 shrink-0' />
        Changes take effect for users after they refresh.
      </div>

      {rowError && <p className='text-sm text-destructive'>{rowError}</p>}

      <Card>
        <CardContent className='p-0'>
          {!selectedTenantId && (
            <div className='py-16 text-center text-sm text-muted-foreground'>
              Select a tenant to manage its feature flags.
            </div>
          )}

          {selectedTenantId && list.isLoading && (
            <div className='flex items-center justify-center py-20'>
              <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
            </div>
          )}

          {selectedTenantId && list.isError && (
            <div className='flex items-center gap-2 px-4 py-6 text-sm text-destructive'>
              <AlertCircle className='h-4 w-4 shrink-0' />
              Could not load feature flags. {(list.error as Error).message}
            </div>
          )}

          {selectedTenantId && list.data && features.length === 0 && (
            <div className='py-16 text-center text-sm text-muted-foreground'>
              No features are defined.
            </div>
          )}

          {selectedTenantId && list.data && features.length > 0 && (
            <ul className='divide-y divide-border'>
              {features.map(feature => (
                <li
                  key={feature.key}
                  className='flex items-center justify-between gap-4 px-4 py-4'
                >
                  <div className='min-w-0'>
                    <p className='text-sm font-medium text-foreground'>{feature.label}</p>
                    <p className='text-sm text-muted-foreground'>{feature.description}</p>
                  </div>
                  <Switch
                    checked={feature.enabled}
                    disabled={setFlag.isPending}
                    aria-label={`Toggle ${feature.label}`}
                    onCheckedChange={value => handleToggle(feature.key, value)}
                  />
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
